import React, {useCallback, useMemo} from 'react';
import PropTypes from 'prop-types';
import {useComponentProps, useEffectEvent} from 'helpers/hooks/utils';
import StyledFieldInlineEdit from 'components/organisms/InlineEdits/FieldInlineEdit/FieldInlineEdit.styles';
import InlineForm from 'components/organisms/Forms/InlineForm/InlineForm';
import {withMemo} from 'helpers/wrapper';
import utils from 'helpers/utils';

const FieldInlineEdit = withMemo(React.forwardRef((props, ref) => {
  const {
    fields,
    fieldData,
    onSubmit,
    onChange,
    onBusy,
    InlineFormProps,
    ...innerProps
  } = useComponentProps(props, 'FieldInlineEdit');

  const onChangeEvent = useEffectEvent(onChange);
  const handleChange = useCallback((field, value, onSuccess, onError) => {
    onChangeEvent?.(field, value, onSuccess, onError);
  }, [onChangeEvent]);

  const onSubmitEvent = useEffectEvent(onSubmit);
  const handleSubmit = useCallback((values, actions) => {
    onSubmitEvent?.(values, actions);
  }, [onSubmitEvent]);

  const fieldsMemo = useMemo(() => {
    return fields.map((field) => {
      const formField = utils.fields2FormFields([field], fieldData)[0];
      return {
        ...field,
        debounce: true,
        FormFieldProps: utils.mergeObjects(formField.FormFieldProps, {
          size: 'smaller',
          variant: !(fields.length > 1) ? 'inlineLabel' : 'staticLabel',
          hiddenLabel: !(fields.length > 1),
          fullWidth: true,
        }, true)
      };
    });
  }, [fields, fieldData]);

  const minWidth = fieldsMemo.reduce((m, f) => {
    return utils.isDefined(f.FormFieldProps?.minWidth) ? Math.max(m ?? 0, f.FormFieldProps?.minWidth) : m
  }, null);

  const maxWidth = fieldsMemo.reduce((m, f) => {
    return utils.isDefined(f.FormFieldProps?.maxWidth) ? Math.max(m ?? 0, f.FormFieldProps?.maxWidth) : m
  }, null);

  return <StyledFieldInlineEdit ref={ref} {...innerProps}
                                $minWidth={minWidth}
                                $maxWidth={maxWidth}>
    <InlineForm fields={fieldsMemo}
                autoTouch={true}
                fieldData={fieldData}
                onChange={handleChange}
                onSubmit={handleSubmit}
                onBusy={onBusy}
                {...InlineFormProps}/>
  </StyledFieldInlineEdit>
}));

FieldInlineEdit.propTypes = {
  className: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.func
  ]),
  fields: PropTypes.array,
  fieldData: PropTypes.object,
  onChange: PropTypes.func,
  onSubmit: PropTypes.func,
  onBusy: PropTypes.func,
  InlineFormProps: PropTypes.object
};

export default FieldInlineEdit;
